import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Link, withRouter } from "react-router";
import api from "../../api/apiUtil";
import Title from "../../components/Layout/Title";

const SurveyResponse = ({ params, currentUser, routes, router }) => {
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [noSurveyMessage, setNoSurveyMessage] = useState(null);

  useEffect(() => {
    loadSurvey();
  }, []);

  const loadSurvey = () => {
    // Load the active survey that the user hasn't responded to yet
    Promise.resolve(api.Survey.getActiveNotResponded())
      .then((surveyData) => {
        // Validate that the survey exists and is active
        const now = new Date();
        if (!surveyData || !surveyData.habilitada || new Date(surveyData.dataInicio) > now || (surveyData.dataFim && new Date(surveyData.dataFim) < now)) {
          toastr.error("Ação não Permitida", "Pesquisa não encontrada ou não está disponível");
          router.push("/secure");
          return;
        }

        setSurvey(surveyData);
        // Initialize responses object
        const initialResponses = {};
        if (surveyData.perguntas) {
          surveyData.perguntas.forEach((question, index) => {
            if (question.tipo === "likert") {
              // For Likert questions, initialize responses for each item
              if (question.configuracao && question.configuracao.itensLikert) {
                question.configuracao.itensLikert.forEach((item, itemIndex) => {
                  initialResponses[`${index}-${itemIndex}`] = "";
                });
              }
            } else {
              // For other question types
              initialResponses[index] = "";
            }
          });
        }
        setResponses(initialResponses);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load survey", err);
        if (err.status === 404) {
          setNoSurveyMessage("Nenhuma pesquisa ativa encontrada");
        } else {
          toastr.error("Erro", "Falha ao carregar a pesquisa");
          router.push("/secure");
        }
        setLoading(false);
      });
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses({
      ...responses,
      [questionIndex]: value,
    });

    // If this question was previously marked as unanswered, remove the highlighting
    const questionContainers = document.querySelectorAll(".question-container");
    // Find which question this is by parsing the index from the key
    const containerIndex = Object.keys(responses).indexOf(questionIndex);
    if (containerIndex >= 0 && questionContainers[containerIndex]) {
      questionContainers[containerIndex].classList.remove("unanswered-required");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate that all required questions are answered
    let allAnswered = true;
    let unansweredQuestions = [];

    survey.perguntas.forEach((question, index) => {
      if (question.obrigatoria) {
        let isAnswered = true;

        if (question.tipo === "likert") {
          // For Likert questions, check if all items have been answered
          if (question.configuracao && question.configuracao.itensLikert) {
            for (let itemIndex = 0; itemIndex < question.configuracao.itensLikert.length; itemIndex++) {
              if (!responses[`${index}-${itemIndex}`]) {
                isAnswered = false;
                break;
              }
            }
          }
        } else {
          // For other question types
          const responseValue = responses[index];
          if (!responseValue || (typeof responseValue === 'string' && responseValue.trim() === "")) {
            isAnswered = false;
          }
        }

        if (!isAnswered) {
          allAnswered = false;
          unansweredQuestions.push(index + 1); // Store 1-based index for user display
        }
      }
    });

    if (!allAnswered) {
      // Highlight unanswered questions by adding a CSS class
      const questionContainers = document.querySelectorAll(".question-container");
      questionContainers.forEach((container, idx) => {
        if (unansweredQuestions.includes(idx + 1)) {
          container.classList.add("unanswered-required");
        } else {
          container.classList.remove("unanswered-required");
        }
      });

      toastr.error("Erro", "Responda todas as perguntas obrigatórias.");
      return;
    }

    // Clear any previous highlighting
    const questionContainers = document.querySelectorAll(".question-container");
    questionContainers.forEach((container) => {
      container.classList.remove("unanswered-required");
    });

    // Format the response data
    const responseData = {
      idPesquisa: survey.id,
      idUsuario: currentUser.id,
      resposta: JSON.stringify({
        perguntas: survey.perguntas.map((question, index) => {
          if (question.tipo === "likert") {
            // For Likert questions, format the responses for each item
            const likertResponses = {};
            if (question.configuracao && question.configuracao.itensLikert) {
              question.configuracao.itensLikert.forEach((item, itemIndex) => {
                likertResponses[itemIndex] = responses[`${index}-${itemIndex}`];
              });
            }
            return {
              ...question,
              resposta: likertResponses,
            };
          } else {
            // For other question types
            return {
              ...question,
              resposta: responses[index],
            };
          }
        }),
      }),
    };

    Promise.resolve(api.Survey.submitResponse(survey.id, responseData))
      .then(() => {
        toastr.success("Sucesso", "Pesquisa respondida com sucesso!");
        // Redirect to home or another page
        router.push("/secure");
      })
      .catch((err) => {
        console.error("Failed to submit survey", err);
        toastr.error("Erro", "Falha ao enviar a resposta da pesquisa");
      });
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!survey) {
    return (
      <div>
        <Title routes={routes} description="Responder Pesquisa de Satisfação" />
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="hpanel">
                <div className="panel-body" style={{ textAlign: "center", padding: "40px" }}>
                  <i className="fa fa-info-circle" style={{ fontSize: "48px", color: "#17a2b8", marginBottom: "16px" }}></i>
                  <h4>{noSurveyMessage || "Pesquisa não encontrada"}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Title routes={routes} description="Responder Pesquisa de Satisfação" />
      <style>
        {`
          .unanswered-required {
            border-left: 4px solid #d9534f;
            padding-left: 15px;
            margin-left: -19px;
          }
          .unanswered-required h4 {
            color: #d9534f;
          }
          textarea.form-control {
            resize: none;
          }
        `}
      </style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="hpanel">
              <div className="panel-heading">
                <h3>{survey.titulo}</h3>
                <p>
                  Período: {new Date(survey.dataInicio).toLocaleDateString("pt-BR")}
                  {survey.dataFim && ` até ${new Date(survey.dataFim).toLocaleDateString("pt-BR")}`}
                </p>
              </div>

              <div className="panel-body">
                <form onSubmit={handleSubmit}>
                  {survey.perguntas &&
                    survey.perguntas.map((question, index) => (
                      <div key={index} className="question-container">
                        <h4>
                          {index + 1}. {question.titulo}
                          {question.obrigatoria && <span className="required">*</span>}
                        </h4>

                        {question.tipo === "text" && (
                          <div className="form-group">
                            <textarea className="form-control" rows="3" value={responses[index] || ""} onChange={(e) => handleResponseChange(index, e.target.value)} />
                          </div>
                        )}

                        {question.tipo === "choice" && (
                          <div className="form-group">
                            {question.configuracao &&
                              question.configuracao.opcoes &&
                              question.configuracao.opcoes.map((option, optionIndex) => (
                                <div key={optionIndex} className="radio">
                                  <label>
                                    <input
                                      type="radio"
                                      name={`question-${index}`}
                                      value={option.texto}
                                      checked={responses[index] === option.texto}
                                      onChange={(e) => handleResponseChange(index, e.target.value)}
                                    />
                                    {option.texto}
                                  </label>
                                </div>
                              ))}
                          </div>
                        )}

                        {question.tipo === "rating" && (
                          <div className="form-group">
                            <div className="rating-stars">
                              {question.configuracao && question.configuracao.escala
                                ? Array.from({ length: question.configuracao.escala }, (_, i) => i + 1).map((star) => (
                                    <span key={star} className={`star ${responses[index] >= star ? "filled" : ""}`} onClick={() => handleResponseChange(index, star)}>
                                      ★
                                    </span>
                                  ))
                                : [1, 2, 3, 4, 5].map((star) => (
                                    <span key={star} className={`star ${responses[index] >= star ? "filled" : ""}`} onClick={() => handleResponseChange(index, star)}>
                                      ★
                                    </span>
                                  ))}
                            </div>
                          </div>
                        )}

                        {question.tipo === "likert" && (
                          <div className="form-group">
                            <table className="likert-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  {question.configuracao &&
                                    question.configuracao.opcoesLikert &&
                                    question.configuracao.opcoesLikert.map((option) => (
                                      <th key={option.id} className="text-center">
                                        {option.texto}
                                      </th>
                                    ))}
                                </tr>
                              </thead>
                              <tbody>
                                {question.configuracao &&
                                  question.configuracao.itensLikert &&
                                  question.configuracao.itensLikert.map((item, itemIndex) => (
                                    <tr key={itemIndex}>
                                      <td>{item.titulo}</td>
                                      {question.configuracao &&
                                        question.configuracao.opcoesLikert &&
                                        question.configuracao.opcoesLikert.map((option) => (
                                          <td key={option.id} className="text-center">
                                            <input
                                              type="radio"
                                              name={`question-${index}-item-${itemIndex}`}
                                              value={option.id}
                                              checked={responses[`${index}-${itemIndex}`] === option.id}
                                              onChange={(e) => handleResponseChange(`${index}-${itemIndex}`, parseInt(e.target.value))}
                                            />
                                          </td>
                                        ))}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                  <div className="form-group d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary mr-2">
                      Enviar Respostas
                    </button>
                    <Link to="/secure" className="btn btn-default">
                      Cancelar
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
});

export default connect(mapStateToProps)(withRouter(SurveyResponse));
